import React, { useState, useEffect, useCallback } from 'react';
import {
  getProjectById,
  createProject,
  updateProject,
  Project,
  ProjectFeature,
  ProjectLinkType
} from '../../firebase/projectService';
import { getActiveCategories, Category } from '../../firebase/categoryService';
import { openCloudinaryUploadWidget } from '../../services/cloudinaryService';
import { motion, AnimatePresence } from 'framer-motion';
import { isYouTubeUrl, getYouTubeVideoId } from '../../utils/mediaUtils';
import {
  PLATFORM_META,
  PLATFORM_ORDER,
  normalizeProjectLinks,
  isValidUrl,
  matchesPlatformHost
} from '../../utils/projectLinks';

interface ProjectFormProps {
  projectId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const initialFormState: Omit<Project, 'id' | 'createdAt' | 'updatedAt'> = {
  title: '',
  description: '',
  longDescription: '',
  image: '',
  gallery: [],
  tags: [],
  technologies: [],
  features: [], // New structured features array
  categories: [], // New multi-category support
  link: '',
  links: [], // Flexible multi-platform live links
  myRole: '', // Highlighted role title for recruiters
  contributions: [], // Bullet list of personal contributions
  github: '',
  company: '',
  companyWebsite: '',
  projectType: 'personal',
  employmentType: 'full-time',
  featured: false,
  order: undefined
};

const ProjectForm: React.FC<ProjectFormProps> = ({ 
  projectId, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState<Omit<Project, 'id' | 'createdAt' | 'updatedAt'>>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const [newTech, setNewTech] = useState('');
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newSubFeature, setNewSubFeature] = useState('');
  const [newContribution, setNewContribution] = useState('');
  const [editingFeatureIndex, setEditingFeatureIndex] = useState<number | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');

  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const categoriesData = await getActiveCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchProject(projectId);
    }
    fetchCategories();
  }, [projectId, fetchCategories]);

  const fetchProject = async (id: string) => {
    setFetchLoading(true);
    try {
      const project = await getProjectById(id);
      if (project) {
        // Handle both new categories array and legacy category field
        let projectCategories: string[] = [];
        if (project.categories && Array.isArray(project.categories)) {
          projectCategories = project.categories;
        } else if (project.category && typeof project.category === 'string') {
          // Convert legacy single category to array
          projectCategories = [project.category];
        }

        // Handle both new structured features and legacy simple features
        let projectFeatures: ProjectFeature[] = [];
        if (project.features && Array.isArray(project.features)) {
          if (project.features.length > 0 && typeof project.features[0] === 'object' && 'name' in project.features[0]) {
            // New structured features
            projectFeatures = project.features as ProjectFeature[];
          } else if (project.features.length > 0 && typeof project.features[0] === 'string') {
            // Legacy string array features - convert to structured format
            const legacyFeatures = project.features as unknown as string[];
            projectFeatures = [{
              name: 'General Features',
              subFeatures: legacyFeatures
            }];
          }
        } else if (project.legacyFeatures && Array.isArray(project.legacyFeatures)) {
          // Handle legacy features field
          projectFeatures = [{
            name: 'General Features',
            subFeatures: project.legacyFeatures
          }];
        }

        setFormData({
          title: project.title,
          description: project.description,
          longDescription: project.longDescription,
          image: project.image,
          gallery: project.gallery || [],
          tags: project.tags,
          technologies: project.technologies,
          features: projectFeatures,
          categories: projectCategories,
          link: project.link || '',
          links: normalizeProjectLinks(project), // Merges legacy `link` + new `links`
          myRole: project.myRole || '',
          contributions: project.contributions || [],
          github: project.github || '',
          company: project.company || '',
          companyWebsite: project.companyWebsite || '',
          projectType: project.projectType || 'personal',
          employmentType: project.employmentType || 'full-time',
          featured: project.featured || false,
          order: project.order
        });
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project data. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Split a comma-separated string into clean, de-duplicated items.
  // Used for bulk-adding tags and technologies (e.g. "React, Node.js, MongoDB").
  const parseCommaList = (input: string): string[] => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const raw of input.split(',')) {
      const item = raw.trim();
      const key = item.toLowerCase();
      if (item && !seen.has(key)) {
        seen.add(key);
        result.push(item);
      }
    }
    return result;
  };

  // Split on top-level commas only (commas inside parentheses are preserved),
  // so feature sub-lists like "Auth (Login, Signup)" stay intact.
  const splitTopLevel = (input: string): string[] => {
    const parts: string[] = [];
    let depth = 0;
    let current = '';
    for (const ch of input) {
      if (ch === '(') {
        depth++;
        current += ch;
      } else if (ch === ')') {
        depth = Math.max(0, depth - 1);
        current += ch;
      } else if (ch === ',' && depth === 0) {
        parts.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    parts.push(current);
    return parts.map(p => p.trim()).filter(Boolean);
  };

  // Parse a single feature segment of the form "Main Feature (sub1, sub2, sub3)".
  // The parentheses (and their comma-separated sub-features) are optional.
  const parseFeatureSegment = (segment: string): ProjectFeature | null => {
    const match = segment.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
    if (match) {
      const name = match[1].trim();
      const subFeatures = match[2].split(',').map(s => s.trim()).filter(Boolean);
      return name ? { name, subFeatures } : null;
    }
    const name = segment.trim();
    return name ? { name, subFeatures: [] } : null;
  };

  const handleAddTag = () => {
    const parsed = parseCommaList(newTag);
    if (parsed.length > 0) {
      setFormData(prev => {
        const existing = new Set(prev.tags.map(t => t.toLowerCase()));
        const additions = parsed.filter(t => !existing.has(t.toLowerCase()));
        return { ...prev, tags: [...prev.tags, ...additions] };
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const handleAddTech = () => {
    const parsed = parseCommaList(newTech);
    if (parsed.length > 0) {
      setFormData(prev => {
        const existing = new Set(prev.technologies.map(t => t.toLowerCase()));
        const additions = parsed.filter(t => !existing.has(t.toLowerCase()));
        return { ...prev, technologies: [...prev.technologies, ...additions] };
      });
      setNewTech('');
    }
  };

  const handleRemoveTech = (index: number) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.filter((_, i) => i !== index)
    }));
  };

  const handleAddContribution = () => {
    // Supports bulk entry: comma-separated contribution points added in one go.
    const parsed = parseCommaList(newContribution);
    if (parsed.length > 0) {
      setFormData(prev => {
        const existing = new Set((prev.contributions || []).map(c => c.toLowerCase()));
        const additions = parsed.filter(c => !existing.has(c.toLowerCase()));
        return { ...prev, contributions: [...(prev.contributions || []), ...additions] };
      });
      setNewContribution('');
    }
  };

  const handleRemoveContribution = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contributions: (prev.contributions || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddFeature = () => {
    // Supports bulk entry: multiple features separated by top-level commas, each with
    // optional sub-features in parentheses, e.g.
    // "Authentication (Login, Signup, OAuth), User Management (Create, Delete), Dashboard"
    const newFeatures = splitTopLevel(newFeatureName)
      .map(parseFeatureSegment)
      .filter((f): f is ProjectFeature => f !== null);
    if (newFeatures.length > 0) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, ...newFeatures]
      }));
      setNewFeatureName('');
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleAddSubFeature = (featureIndex: number) => {
    // Supports bulk entry: comma-separated sub-features added in one go.
    const parsed = parseCommaList(newSubFeature);
    if (parsed.length > 0) {
      setFormData(prev => ({
        ...prev,
        features: prev.features.map((feature, i) => {
          if (i !== featureIndex) return feature;
          const existing = new Set(feature.subFeatures.map(s => s.toLowerCase()));
          const additions = parsed.filter(s => !existing.has(s.toLowerCase()));
          return { ...feature, subFeatures: [...feature.subFeatures, ...additions] };
        })
      }));
      setNewSubFeature('');
    }
  };

  const handleRemoveSubFeature = (featureIndex: number, subFeatureIndex: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) =>
        i === featureIndex ? { ...feature, subFeatures: feature.subFeatures.filter((_, j) => j !== subFeatureIndex) } : feature
      )
    }));
  };

  const handleCategoryToggle = (categoryName: string) => {
    setFormData(prev => {
      const isSelected = prev.categories.includes(categoryName);
      if (isSelected) {
        // Remove category
        return {
          ...prev,
          categories: prev.categories.filter(cat => cat !== categoryName)
        };
      } else {
        // Add category
        return {
          ...prev,
          categories: [...prev.categories, categoryName]
        };
      }
    });
  };

  // Toggle a live-link platform on/off. Ticking adds an empty entry (URL filled below);
  // un-ticking removes it from the links array.
  const handleToggleLinkPlatform = (type: ProjectLinkType) => {
    setFormData(prev => {
      const links = prev.links || [];
      const exists = links.some(l => l.type === type);
      if (exists) {
        return { ...prev, links: links.filter(l => l.type !== type) };
      }
      return { ...prev, links: [...links, { type, url: '' }] };
    });
  };

  // Update the URL for a ticked platform.
  const handleLinkUrlChange = (type: ProjectLinkType, url: string) => {
    setFormData(prev => ({
      ...prev,
      links: (prev.links || []).map(l => (l.type === type ? { ...l, url } : l))
    }));
  };

  const uploadMainImage = () => {
    setImageUploading(true);
    
    openCloudinaryUploadWidget({
      folder: 'portfolio_projects/main',
      multiple: false,
      onSuccess: (results) => {
        if (results.length > 0) {
          setFormData(prev => ({
            ...prev,
            image: results[0]
          }));
        }
        setImageUploading(false);
      },
      onError: (error) => {
        console.error('Error uploading image:', error);
        setError('Failed to upload image. Please try again.');
        setImageUploading(false);
      }
    });
  };

  const uploadGalleryImages = () => {
    setGalleryUploading(true);
    
    openCloudinaryUploadWidget({
      folder: 'portfolio_projects/gallery',
      multiple: true,
      maxFiles: 5,
      onSuccess: (urls) => {
        setFormData(prev => ({
          ...prev,
          gallery: [...(prev.gallery || []), ...urls]
        }));
        setGalleryUploading(false);
      },
      onError: (error) => {
        console.error('Error uploading gallery images:', error);
        setError('Failed to upload gallery images. Please try again.');
        setGalleryUploading(false);
      }
    });
  };

  const removeGalleryImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery?.filter((_, i) => i !== index) || []
    }));
  };

  // Add URL (image, GIF, or YouTube video) to gallery
  const addGalleryUrl = () => {
    if (newGalleryUrl.trim()) {
      const url = newGalleryUrl.trim();
      
      // Basic URL validation
      try {
        new URL(url);
        setFormData(prev => ({
          ...prev,
          gallery: [...(prev.gallery || []), url]
        }));
        setNewGalleryUrl('');
      } catch (error) {
        setError('Please enter a valid URL');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Form validation
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Description is required');
      }
      if (!formData.longDescription.trim()) {
        throw new Error('Detailed description is required');
      }
      // Main image is now optional - will use placeholder if not provided
      if (formData.tags.length === 0) {
        throw new Error('At least one tag is required');
      }
      if (formData.technologies.length === 0) {
        throw new Error('At least one technology is required');
      }
      if (formData.features.length === 0) {
        throw new Error('At least one feature is required');
      }
      if (formData.categories.length === 0) {
        throw new Error('At least one category is required');
      }
      if ((formData.projectType === 'company' || formData.projectType === 'freelance') && !formData.company?.trim()) {
        throw new Error('Company/Client name is required for company and freelance projects');
      }

      // Validate live links: every ticked platform must have a valid, non-empty URL.
      const ticked = formData.links || [];
      for (const link of ticked) {
        const meta = PLATFORM_META[link.type];
        if (!link.url.trim()) {
          throw new Error(`Please enter a URL for ${meta.label}, or untick it.`);
        }
        if (!isValidUrl(link.url.trim())) {
          throw new Error(`The ${meta.label} URL is not a valid URL.`);
        }
      }

      // Normalize the links we persist: trim URLs and drop any empty entries.
      const cleanedLinks = ticked
        .map(l => ({ type: l.type, url: l.url.trim() }))
        .filter(l => l.url);

      // Keep writing the legacy `link` (website) during rollout for safety.
      const websiteLink = cleanedLinks.find(l => l.type === 'website');
      const payload = {
        ...formData,
        links: cleanedLinks,
        link: websiteLink ? websiteLink.url : ''
      };

      if (projectId) {
        // Update existing project
        await updateProject(projectId, payload);
      } else {
        // Create new project
        await createProject(payload);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err instanceof Error ? err.message : 'Failed to save project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Framer motion animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        >
          <motion.div
            variants={modalVariants}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">
                  {projectId ? 'Edit Project' : 'Create New Project'}
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {fetchLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {error && (
                    <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Project Title*
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                        placeholder="E.g., Portfolio Website"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Categories* (Select all that apply - e.g., Backend + Frontend)
                      </label>
                      {categoriesLoading ? (
                        <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center">
                          <div className="animate-spin mr-2 h-4 w-4 border-b-2 border-primary-600 rounded-full"></div>
                          <span className="text-gray-600 dark:text-gray-400">Loading categories...</span>
                        </div>
                      ) : categories.length > 0 ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {categories.map((category) => {
                              const isSelected = formData.categories.includes(category.name);
                              return (
                                <div
                                  key={category.id}
                                  className={`relative flex items-center p-4 border-2 rounded-lg transition-all ${
                                    isSelected
                                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    id={`category-${category.id}`}
                                    checked={isSelected}
                                    onChange={() => handleCategoryToggle(category.name)}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                                  />
                                  <label
                                    htmlFor={`category-${category.id}`}
                                    className="ml-3 flex items-center flex-1 cursor-pointer"
                                  >
                                    <div
                                      className="w-3 h-3 rounded-full mr-2"
                                      style={{ backgroundColor: category.color }}
                                    ></div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {category.name}
                                      </span>
                                      {category.description && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                          {category.description}
                                        </p>
                                      )}
                                    </div>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                          {formData.categories.length > 0 && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Selected categories:</p>
                              <div className="flex flex-wrap gap-2">
                                {formData.categories.map((categoryName, index) => {
                                  const category = categories.find(c => c.name === categoryName);
                                  return (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
                                    >
                                      <div
                                        className="w-2 h-2 rounded-full mr-2"
                                        style={{ backgroundColor: category?.color || '#3B82F6' }}
                                      ></div>
                                      {categoryName}
                                      <button
                                        type="button"
                                        onClick={() => handleCategoryToggle(categoryName)}
                                        className="ml-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700">
                          <span className="text-gray-600 dark:text-gray-400">No categories available. Please create categories first.</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Short Description* (Elevator pitch, shown in listings)
                    </label>
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      placeholder="A brief description of your project"
                      maxLength={120}
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formData.description.length}/120 characters
                    </p>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Detailed Description*
                    </label>
                    <textarea
                      name="longDescription"
                      value={formData.longDescription}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      placeholder="Provide a detailed description of your project"
                      rows={5}
                      required
                    />
                  </div>

                  {/* My Role & Contributions — highlighted for recruiters */}
                  <div className="mb-6 p-4 rounded-lg border-2 border-primary-200 dark:border-primary-900/40 bg-gradient-to-br from-primary-50 to-transparent dark:from-primary-900/10">
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                      ⭐ My Role
                    </label>
                    <input
                      type="text"
                      name="myRole"
                      value={formData.myRole}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      placeholder="e.g., Lead Full-Stack Developer · Backend Architect · Solo Developer"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      A short title shown as a highlighted badge so recruiters instantly see your role.
                    </p>

                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-1">
                      Key Contributions (What I did)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      💡 Tip: paste a comma-separated list to add many at once — e.g.
                      <span className="font-medium"> Designed the REST API, Built CI/CD pipeline, Led a team of 4</span>
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(formData.contributions || []).map((item, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md text-sm flex items-center"
                        >
                          {item}
                          <button
                            type="button"
                            onClick={() => handleRemoveContribution(index)}
                            className="ml-1 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex">
                      <input
                        type="text"
                        value={newContribution}
                        onChange={(e) => setNewContribution(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddContribution())}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                        placeholder="Add one contribution or a comma-separated list"
                      />
                      <button
                        type="button"
                        onClick={handleAddContribution}
                        className="px-3 py-2 bg-primary-600 text-white rounded-r-md hover:bg-primary-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      GitHub Repository URL
                    </label>
                    <input
                      type="url"
                      name="github"
                      value={formData.github}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      placeholder="https://github.com/yourusername/repo"
                    />
                  </div>

                  {/* Live Links - select one or more platforms, then provide a URL for each */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Live Links (Optional) — tick the platforms this project is available on
                    </label>
                    <div className="space-y-3">
                      {PLATFORM_ORDER.map((type) => {
                        const meta = PLATFORM_META[type];
                        const entry = (formData.links || []).find(l => l.type === type);
                        const isSelected = !!entry;
                        const Icon = meta.Icon;
                        const hostWarning =
                          isSelected &&
                          entry!.url.trim() !== '' &&
                          isValidUrl(entry!.url.trim()) &&
                          !matchesPlatformHost(type, entry!.url.trim());

                        return (
                          <div
                            key={type}
                            className={`border-2 rounded-lg transition-all ${
                              isSelected
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <div
                              className="flex items-center p-4 cursor-pointer"
                              onClick={() => handleToggleLinkPlatform(type)}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleLinkPlatform(type)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 flex-shrink-0" style={{ color: meta.color }}>
                                <Icon className="w-5 h-5" />
                              </span>
                              <span
                                className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100"
                                style={isSelected ? { color: meta.color } : undefined}
                              >
                                {meta.label}
                              </span>
                            </div>

                            {isSelected && (
                              <div className="px-4 pb-4">
                                <input
                                  type="url"
                                  value={entry!.url}
                                  onChange={(e) => handleLinkUrlChange(type, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                                  placeholder={meta.placeholder}
                                />
                                {hostWarning && (
                                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                    ⚠️ This doesn't look like a {meta.label} URL (expected{' '}
                                    {meta.hostHints.join(' or ')}). You can still save it.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Project Type and Company Information */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Project Type*
                    </label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      required
                    >
                      <option value="personal">Personal Project</option>
                      <option value="company">Company Project</option>
                      <option value="freelance">Freelance Project</option>
                      <option value="open-source">Open Source Contribution</option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Select the type to help provide appropriate context for recruiters
                    </p>
                  </div>

                  {/* Company Information - Show only for company/freelance projects */}
                  {(formData.projectType === 'company' || formData.projectType === 'freelance') && (
                    <div className="space-y-6 mb-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Company/Client Name*
                          </label>
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                            placeholder="e.g., Google, Meta, Startup Inc."
                            required={formData.projectType === 'company' || formData.projectType === 'freelance'}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formData.projectType === 'company' ? 'Company where you worked on this project' : 'Client you worked for'}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Company/Client Website (Optional)
                          </label>
                          <input
                            type="url"
                            name="companyWebsite"
                            value={formData.companyWebsite}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                            placeholder="https://company.com"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Adds credibility and context for recruiters
                          </p>
                        </div>
                      </div>

                      {/* Employment Type - Show only for company projects */}
                      {formData.projectType === 'company' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Employment Type*
                          </label>
                          <select
                            name="employmentType"
                            value={formData.employmentType}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                            required={formData.projectType === 'company'}
                          >
                            <option value="full-time">Full-time</option>
                            <option value="part-time">Part-time</option>
                            <option value="contract">Contract</option>
                            <option value="internship">Internship</option>
                          </select>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Specify your employment arrangement at the company
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="col-span-full mt-4">
                    <div className="flex items-center space-x-3">
                      <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                          type="checkbox"
                          name="featured"
                          id="featured"
                          checked={formData.featured}
                          onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                          className="hidden"
                        />
                        <label
                          htmlFor="featured"
                          className={`block overflow-hidden h-6 rounded-full bg-gray-300 dark:bg-gray-700 cursor-pointer ${
                            formData.featured ? "bg-primary-600 dark:bg-primary-500" : ""
                          }`}
                        >
                          <span
                            className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-300 ease-in-out ${
                              formData.featured ? "translate-x-4" : "translate-x-0"
                            }`}
                          />
                        </label>
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Featured Project
                      </span>
                      <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
                        (Will appear on homepage)
                      </span>
                    </div>
                  </div>

                  {/* Display Order / Ranking */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Display Order (Optional)
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          order: value === '' ? undefined : parseInt(value, 10)
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      placeholder="e.g., 1, 2, 3..."
                      min="1"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Lower numbers appear first (e.g., 1 = first position). Leave empty to show at the end by creation date.
                    </p>
                  </div>

                  {/* Tags Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tags* (E.g., React, Mobile App)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      💡 Tip: paste a comma-separated list to add many at once — e.g. <span className="font-medium">React, Mobile App, UI/UX, Open Source</span>
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-md text-sm flex items-center"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(index)}
                            className="ml-1 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                        placeholder="Add one tag or a comma-separated list"
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="px-3 py-2 bg-primary-600 text-white rounded-r-md hover:bg-primary-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Technologies Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Technologies Used* (E.g., React, Node.js, MongoDB)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      💡 Tip: paste a comma-separated list to add many at once — e.g. <span className="font-medium">React, Node.js, MongoDB, Docker, AWS</span>
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.technologies.map((tech, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md text-sm flex items-center"
                        >
                          {tech}
                          <button
                            type="button"
                            onClick={() => handleRemoveTech(index)}
                            className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex">
                      <input
                        type="text"
                        value={newTech}
                        onChange={(e) => setNewTech(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTech())}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                        placeholder="Add one technology or a comma-separated list"
                      />
                      <button
                        type="button"
                        onClick={handleAddTech}
                        className="px-3 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Key Features
                    </label>
                    <div className="mb-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-md text-xs text-gray-600 dark:text-gray-400">
                      💡 <span className="font-medium">Bulk add:</span> type several features at once, separated by commas. Put each feature's
                      sub-features in parentheses right after it:
                      <code className="block mt-1 px-2 py-1 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-[11px] text-gray-800 dark:text-gray-200">
                        Authentication (Login, Signup, OAuth), User Management (Create, Delete), Dashboard
                      </code>
                      <span className="block mt-1">Features without parentheses are added with no sub-features. You can still refine them below.</span>
                    </div>

                    {/* Existing Features */}
                    <div className="space-y-4 mb-4">
                      {formData.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                          {/* Feature Header */}
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium text-gray-900 dark:text-white">{feature.name}</h4>
                            <button
                              type="button"
                              onClick={() => handleRemoveFeature(featureIndex)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Sub-features */}
                          <div className="space-y-2 mb-3">
                            {feature.subFeatures.map((subFeature, subIndex) => (
                              <div key={subIndex} className="flex justify-between items-center px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                                <span className="text-sm text-gray-700 dark:text-gray-300">• {subFeature}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSubFeature(featureIndex, subIndex)}
                                  className="text-red-500 hover:text-red-700 dark:text-red-400"
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                          
                          {/* Add Sub-feature */}
                          <div className="flex">
                            <input
                              type="text"
                              value={editingFeatureIndex === featureIndex ? newSubFeature : ''}
                              onChange={(e) => {
                                setNewSubFeature(e.target.value);
                                setEditingFeatureIndex(featureIndex);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddSubFeature(featureIndex);
                                  setEditingFeatureIndex(null);
                                }
                              }}
                              placeholder="Add a sub-feature (or comma-separated list)..."
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                handleAddSubFeature(featureIndex);
                                setEditingFeatureIndex(null);
                              }}
                              className="px-4 py-2 bg-primary-600 text-white rounded-r-md hover:bg-primary-700 transition-colors text-sm"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add New Feature */}
                    <div className="flex">
                      <input
                        type="text"
                        value={newFeatureName}
                        onChange={(e) => setNewFeatureName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                        placeholder="e.g., Authentication (Login, Signup), User Management (Create, Delete)"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
                      />
                      <button
                        type="button"
                        onClick={handleAddFeature}
                        className="px-4 py-2 bg-primary-600 text-white rounded-r-md hover:bg-primary-700 transition-colors"
                      >
                        Add Feature
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Add one or many feature categories at once, then refine the sub-features under each below.
                    </p>
                  </div>

                  {/* Main Image Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Main Image (Optional)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      If no image is uploaded, a default "TKB" placeholder will be shown
                    </p>
                    {formData.image ? (
                      <div className="relative mb-3 w-full h-48 rounded-md overflow-hidden">
                        <img
                          src={formData.image}
                          alt="Project main"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                          className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={uploadMainImage}
                        disabled={imageUploading}
                        className="w-full h-48 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex items-center justify-center hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                      >
                        {imageUploading ? (
                          <div className="text-center">
                            <div className="animate-spin mx-auto mb-2 h-8 w-8 border-b-2 border-primary-600 rounded-full"></div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Uploading...
                            </p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="mx-auto h-12 w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              />
                            </svg>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                              Click to upload main image
                            </p>
                          </div>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Gallery Images/Videos Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Gallery Media (Optional)
                    </label>
                    
                    {/* Existing gallery items */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                      {formData.gallery?.map((item, index) => {
                        const isYouTube = isYouTubeUrl(item);
                        const videoId = isYouTube ? getYouTubeVideoId(item) : null;
                        const isGif = item.toLowerCase().endsWith('.gif');
                        
                        return (
                          <div key={index} className="relative h-32 rounded-md overflow-hidden">
                            {isYouTube && videoId ? (
                              // YouTube video thumbnail
                              <div className="relative w-full h-full bg-black flex items-center justify-center">
                                <img
                                  src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                  alt={`YouTube video ${index + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Fallback to hqdefault if maxresdefault doesn't exist
                                    e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z"/>
                                    </svg>
                                  </div>
                                </div>
                                <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                                  📺 YouTube
                                </div>
                              </div>
                            ) : (
                              // Regular image or GIF
                              <img
                                src={item}
                                alt={`Gallery ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            )}
                            
                            {isGif && !isYouTube && (
                              <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                                🎬 GIF
                              </div>
                            )}
                            
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                      
                      {/* Upload button for images/GIFs */}
                      <button
                        type="button"
                        onClick={uploadGalleryImages}
                        disabled={galleryUploading}
                        className="h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex items-center justify-center hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                      >
                        {galleryUploading ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        ) : (
                          <div className="text-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="mx-auto h-8 w-8 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                              Upload Images
                            </p>
                          </div>
                        )}
                      </button>
                    </div>
                    
                    {/* Manual URL input for YouTube videos */}
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={newGalleryUrl}
                          onChange={(e) => setNewGalleryUrl(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addGalleryUrl())}
                          placeholder="Add YouTube video URL or image URL..."
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 text-sm"
                        />
                        <button
                          type="button"
                          onClick={addGalleryUrl}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm whitespace-nowrap"
                        >
                          📺 Add URL
                        </button>
                      </div>
                      
                      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                        <p><strong>Supported formats:</strong></p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li><strong>Images:</strong> Upload via button above (JPG, PNG, WebP, etc.)</li>
                          <li><strong>GIFs:</strong> Upload via button above for animated demos</li>
                          <li><strong>YouTube Videos:</strong> Paste YouTube URLs (youtube.com/watch?v=... or youtu.be/...)</li>
                        </ul>
                        <p className="mt-2"><strong>Examples:</strong></p>
                        <ul className="list-disc list-inside space-y-1 ml-2 text-gray-400">
                          <li>https://youtube.com/watch?v=VIDEO_ID</li>
                          <li>https://youtu.be/VIDEO_ID</li>
                          <li>https://example.com/image.jpg</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4 mt-8">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin mr-2 h-4 w-4 border-b-2 border-white rounded-full"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <span>{projectId ? 'Update Project' : 'Create Project'}</span>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectForm; 